/**
 * Nuria Morera — Cart Drawer Conversion Layer
 *
 * Keeps Shopify's native cart forms and events intact while adding adaptive
 * recommendation quick-add, accessible feedback and first-party CRO events.
 * It never emits standard ecommerce events, avoiding Meta Pixel duplication.
 */

const NMCART = {
  lastCartTotal: null,
  drawerIsOpen: false,
  initTimer: null,

  init() {
    const meta = this.getCartMeta();
    this.lastCartTotal = meta.cart_value;
    this.watchDrawer();
    this.watchCartUpdates();
    this.initExperience();
  },

  initExperience() {
    this.initRecommendations();
    this.initDiscountCode();
    this.initCheckoutTracking();
    this.syncThresholdState();
  },

  queueExperienceInit() {
    window.clearTimeout(this.initTimer);
    this.initTimer = window.setTimeout(() => this.initExperience(), 40);
  },

  watchDrawer() {
    const drawer = document.querySelector('cart-drawer');
    const cartRoot = document.getElementById('CartDrawer');
    if (!drawer || !cartRoot) return;

    const drawerObserver = new MutationObserver(() => {
      const isOpen = drawer.classList.contains('active');
      if (isOpen && !this.drawerIsOpen) {
        this.drawerIsOpen = true;
        this.publish('nm_cart_drawer_view', this.getCartMeta());
        this.initExperience();
      } else if (!isOpen) {
        this.drawerIsOpen = false;
      }
    });
    drawerObserver.observe(drawer, { attributes: true, attributeFilter: ['class'] });

    const contentObserver = new MutationObserver(() => this.queueExperienceInit());
    contentObserver.observe(cartRoot, { childList: true, subtree: true });
  },

  watchCartUpdates() {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

    subscribe(PUB_SUB_EVENTS.cartUpdate, (event = {}) => {
      const cartData = event.cartData;
      const inner = document.querySelector('#CartDrawer .drawer__inner');
      if (cartData && inner) {
        inner.dataset.cartTotalCents = String(cartData.total_price || 0);
        inner.dataset.cartItemCount = String(cartData.item_count || 0);
      }
      this.queueExperienceInit();
    });
  },

  initRecommendations() {
    const container = document.querySelector('.cart-drawer__complementary');
    if (!container) return;

    if (document.querySelector('cart-drawer')?.classList.contains('active')) {
      container.querySelectorAll('[data-nm-recommendation]:not([data-impression-tracked])').forEach((card) => {
        card.dataset.impressionTracked = 'true';
        this.publish('nm_cart_recommendation_impression', {
          ...this.getCartMeta(),
          product_id: card.dataset.productId || '',
          product_handle: card.dataset.productHandle || '',
          product_title: card.dataset.productTitle || '',
          variant_id: card.dataset.variantId || '',
          recommendation_value: this.toNumber(card.dataset.priceCents),
          unlocks_threshold: card.dataset.unlocksThreshold === 'true',
        });
      });
    }

    container.querySelectorAll('.cart-drawer__rec-add:not([data-bound])').forEach((button) => {
      button.dataset.bound = 'true';
      button.addEventListener('click', (event) => this.addRecommendation(event.currentTarget));
    });
  },

  async addRecommendation(button) {
    const variantId = this.toNumber(button.dataset.variantId);
    if (!variantId || button.disabled) return;

    const originalText = button.textContent.trim();
    const addedText = button.dataset.addedLabel || 'Añadido';
    const metaBefore = this.getCartMeta();
    const errorRegion = document.getElementById('CartDrawer-CartErrors');

    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    button.textContent = 'Añadiendo…';
    if (errorRegion) errorRegion.textContent = '';

    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 1 }),
      });

      if (!response.ok) {
        const errorPayload = await response.json().catch(() => ({}));
        throw new Error(errorPayload.description || 'No pudimos agregar esta pieza.');
      }

      button.textContent = addedText;
      this.publish('nm_cart_recommendation_add', {
        ...metaBefore,
        product_id: button.dataset.productId || '',
        product_handle: button.dataset.productHandle || '',
        product_title: button.dataset.productTitle || '',
        variant_id: String(variantId),
        recommendation_value: this.toNumber(button.dataset.priceCents),
        unlocks_threshold: button.dataset.unlocksThreshold === 'true',
      });

      await this.refreshDrawer();
    } catch (error) {
      button.disabled = false;
      button.removeAttribute('aria-busy');
      button.textContent = originalText;
      if (errorRegion) errorRegion.textContent = error.message || 'No pudimos agregar esta pieza. Intenta de nuevo.';
    }
  },

  async refreshDrawer() {
    const response = await fetch(`${window.location.pathname}?sections=cart-drawer,cart-icon-bubble`, {
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error('No pudimos actualizar tu selección.');

    const sections = await response.json();
    const parser = new DOMParser();
    const drawer = document.querySelector('cart-drawer');
    const currentInner = drawer?.querySelector('.drawer__inner');

    if (sections['cart-drawer'] && currentInner) {
      const documentFragment = parser.parseFromString(sections['cart-drawer'], 'text/html');
      const newInner = documentFragment.querySelector('.drawer__inner');
      if (newInner) {
        [...newInner.attributes].forEach((attribute) => {
          if (attribute.name.startsWith('data-')) currentInner.setAttribute(attribute.name, attribute.value);
        });
        currentInner.innerHTML = newInner.innerHTML;
      }
    }

    if (sections['cart-icon-bubble']) {
      const iconBubble = document.querySelector('#cart-icon-bubble');
      const iconDocument = parser.parseFromString(sections['cart-icon-bubble'], 'text/html');
      const newBubble = iconDocument.querySelector('.shopify-section');
      if (iconBubble && newBubble) iconBubble.innerHTML = newBubble.innerHTML;
    }

    drawer?.classList.remove('is-empty');
    this.initExperience();
  },

  initCheckoutTracking() {
    const checkout = document.getElementById('CartDrawer-Checkout');
    if (!checkout || checkout.dataset.bound) return;

    checkout.dataset.bound = 'true';
    checkout.addEventListener('click', () => {
      this.publish('nm_cart_checkout_click', this.getCartMeta());
    });
  },

  syncThresholdState() {
    const meta = this.getCartMeta();
    if (this.lastCartTotal !== null && this.lastCartTotal < meta.threshold && meta.cart_value >= meta.threshold) {
      this.publish('nm_cart_threshold_unlocked', meta);
    }
    this.lastCartTotal = meta.cart_value;
  },

  getCartMeta() {
    const inner = document.querySelector('#CartDrawer .drawer__inner');
    const total = this.toNumber(inner?.dataset.cartTotalCents);
    const threshold = this.toNumber(inner?.dataset.cartThresholdCents);
    return {
      cart_value: total,
      threshold,
      gap_to_threshold: Math.max(threshold - total, 0),
      item_count: this.toNumber(inner?.dataset.cartItemCount),
      currency: inner?.dataset.cartCurrency || 'MXN',
    };
  },

  publish(name, detail = {}) {
    const payload = { event: name, ...detail };
    document.dispatchEvent(new CustomEvent(name, { detail }));
    if (Array.isArray(window.dataLayer)) window.dataLayer.push(payload);
  },

  initDiscountCode() {
    const input = document.getElementById('CartDrawer-DiscountCode');
    const applyButton = document.getElementById('CartDrawer-ApplyDiscount');
    if (!input || !applyButton || applyButton.dataset.bound) return;

    applyButton.dataset.bound = 'true';
    applyButton.addEventListener('click', () => this.applyDiscount(input, applyButton));
    input.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') this.applyDiscount(input, applyButton);
    });
  },

  async applyDiscount(input, button) {
    const code = input.value.trim();
    const message = document.getElementById('CartDrawer-DiscountMessage');
    if (!code) {
      if (message) {
        message.textContent = 'Ingresa un código.';
        message.className = 'cart-drawer__discount-message cart-drawer__discount-message--error';
      }
      return;
    }

    const originalText = button.textContent;
    button.textContent = '…';
    button.disabled = true;
    try {
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ discount: code }),
      });
      if (!response.ok) throw new Error();
      if (message) {
        message.textContent = `Código "${code}" aplicado.`;
        message.className = 'cart-drawer__discount-message cart-drawer__discount-message--success';
      }
    } catch {
      if (message) {
        message.textContent = 'No pudimos aplicar el código. Revisa e intenta de nuevo.';
        message.className = 'cart-drawer__discount-message cart-drawer__discount-message--error';
      }
    } finally {
      button.textContent = originalText;
      button.disabled = false;
    }
  },

  toNumber(value) {
    const number = Number.parseInt(value || '0', 10);
    return Number.isFinite(number) ? number : 0;
  },
};

document.addEventListener('DOMContentLoaded', () => NMCART.init());
